import * as DocumentPicker from "expo-document-picker";
import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, TextInput, View } from "react-native";

import { getClientCompetition } from "../../../../../../lib/clientCompetitions";
import { getDeliverable, updateDeliverable, uploadDeliverableTemplate } from "../../../../../../lib/deliverables";
import { useAuth } from "../../../../../../lib/authContext";
import { isAdminRole } from "../../../../../../lib/auth";
import { ADMIN_EDIT_STATUSES } from "../../../../../../lib/adminCaseEdit";
import { documentPickerTypesForFileType } from "../../../../../../lib/submissions";
import type {
  ClientCompetitionListRow,
  Deliverable,
  DeliverableFileType,
} from "../../../../../../types/database";
import { COLORS, FONTS } from "../../../../../../lib/brand";
import { parseDateTimeField } from "../../../../../../lib/datetime";
import { PANE_THEMES } from "../../../../../../components/brand/paneThemes";
import { FriendlyDateTime } from "../../../../../../components/client/FriendlyDateTime";

const THEME = PANE_THEMES.Client;
const BLACK = THEME.pageBg;
const OFF_BLACK = THEME.surfaceBg;
const AMBER = THEME.accent;
const RED = THEME.destructive;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const BLACK_SURFACE = "rgba(0,0,0,0.4)";

const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

const FILE_TYPES: DeliverableFileType[] = [
  "pdf",
  "excel",
  "powerpoint",
  "word",
  "csv",
  "zip",
  "image",
  "video",
  "any",
];

const FILE_LABEL: Record<DeliverableFileType, string> = {
  pdf: "PDF",
  excel: "Excel",
  powerpoint: "PowerPoint",
  word: "Word",
  csv: "CSV",
  zip: "Zip",
  image: "Image",
  video: "Video",
  any: "Any file",
};

function rubricToString(r: Deliverable["rubric"]): string {
  if (r == null) return "";
  if (typeof r === "string") return r;
  try {
    return JSON.stringify(r);
  } catch {
    return String(r);
  }
}

export default function EditDeliverableScreen() {
  const { id: competitionId, deliverableId } = useLocalSearchParams<{ id: string; deliverableId: string }>();
  const { role } = useAuth();
  const adminMode = isAdminRole(role);
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<Deliverable | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fileType, setFileType] = useState<DeliverableFileType>("pdf");
  const [rubric, setRubric] = useState("");
  const [pointValue, setPointValue] = useState("10");
  const [isScored, setIsScored] = useState(true);
  const [isLockedGate, setIsLockedGate] = useState(false);
  const [allowRevisions, setAllowRevisions] = useState(true);
  const [opensAt, setOpensAt] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [templateAsset, setTemplateAsset] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draftOnly, setDraftOnly] = useState(true);
  const [comp, setComp] = useState<ClientCompetitionListRow | null>(null);

  useEffect(() => {
    void (async () => {
      if (typeof deliverableId !== "string" || typeof competitionId !== "string") {
        setLoading(false);
        return;
      }
      setLoading(true);
      const c = await getClientCompetition(competitionId);
      setComp(c);
      // `draftOnly` gates every field + the save. Clients edit only while a
      // draft; admins may also fix deliverables on a submitted/under_review/
      // approved case (admin RLS authorizes the write).
      setDraftOnly(
        c?.status === "draft" || (adminMode && !!c && ADMIN_EDIT_STATUSES.includes(c.status)),
      );
      const d = await getDeliverable(deliverableId);
      setRow(d);
      if (d && d.competition_id === competitionId) {
        setName(d.name);
        setDescription(d.description ?? "");
        setFileType(d.file_type);
        setRubric(rubricToString(d.rubric));
        setPointValue(d.point_value != null ? String(d.point_value) : "0");
        setIsScored(d.is_scored);
        setIsLockedGate(d.is_locked_gate);
        setAllowRevisions(d.allow_revisions);
        setOpensAt(d.opens_at ? String(d.opens_at).slice(0, 16) : "");
        setDueAt(d.due_at ? String(d.due_at).slice(0, 16) : "");
      }
      setLoading(false);
    })();
  }, [competitionId, deliverableId]);

  function goBack() {
    if (router.canGoBack()) router.back();
    else if (typeof competitionId === "string") router.replace(`/client/competition/${competitionId}` as Href);
    else router.replace("/client" as Href);
  }

  async function pickTemplate() {
    const res = await DocumentPicker.getDocumentAsync({
      type: documentPickerTypesForFileType(fileType),
      copyToCacheDirectory: true,
    });
    if (!res.canceled && res.assets?.[0]) setTemplateAsset(res.assets[0]);
  }

  async function onSave() {
    if (typeof deliverableId !== "string" || typeof competitionId !== "string" || !row) return;
    if (!name.trim()) {
      setErr("Give the deliverable a name first.");
      return;
    }
    if (!draftOnly) {
      setErr("Deliverables can only be edited while the case is a draft.");
      return;
    }

    // Validate the date fields before touching the DB so an unparseable typo
    // shows a friendly message instead of crashing the save with RangeError.
    const opensParsed = parseDateTimeField(opensAt, "Opens at");
    if (!opensParsed.ok) {
      setErr(opensParsed.msg);
      return;
    }
    const dueParsed = parseDateTimeField(dueAt, "Due at");
    if (!dueParsed.ok) {
      setErr(dueParsed.msg);
      return;
    }

    setSaving(true);
    setErr(null);
    const pv = parseInt(pointValue, 10);
    const { error } = await updateDeliverable(deliverableId, {
      name: name.trim(),
      description: description.trim() || null,
      file_type: fileType,
      rubric: (rubric.trim() || null) as unknown as Deliverable["rubric"],
      point_value: isScored && Number.isFinite(pv) ? pv : null,
      is_scored: isScored,
      is_locked_gate: isLockedGate,
      allow_revisions: allowRevisions,
      opens_at: opensParsed.value,
      due_at: dueParsed.value,
    });
    if (error) {
      setSaving(false);
      setErr(error.message);
      return;
    }

    if (templateAsset) {
      const { error: upErr } = await uploadDeliverableTemplate(competitionId, deliverableId, templateAsset);
      if (upErr) {
        setSaving(false);
        setErr(upErr.message);
        return;
      }
    }

    setSaving(false);
    router.replace(`/client/competition/${competitionId}` as Href);
  }

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", paddingVertical: 80 }}>
        <ActivityIndicator size="large" color={AMBER} />
      </View>
    );
  }

  if (!row || row.competition_id !== competitionId) {
    return (
      <View style={{ alignSelf: "center", maxWidth: 600, paddingHorizontal: 24, paddingTop: 32 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, color: W, fontSize: 14 }}>
          Deliverable not found.
        </Text>
        <Pressable onPress={goBack} style={[{ marginTop: 16, alignSelf: "flex-start" }, cursorWeb]}>
          <Text style={{ fontFamily: FONTS.bodyMedium, color: AMBER, fontSize: 12 }}>{"← Back to case"}</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }} keyboardShouldPersistTaps="handled">
        <View style={{ alignSelf: "center", width: "100%", maxWidth: 760, paddingHorizontal: 24, paddingTop: 28 }}>
          {/* Back link */}
          <Pressable onPress={goBack} style={[{ alignSelf: "flex-start", paddingVertical: 6 }, cursorWeb]}>
            <Text
              style={{
                fontFamily: FONTS.bodyMedium,
                fontSize: 12,
                color: AMBER,
                letterSpacing: 0.4,
              }}
            >
              {"← Back to case"}
            </Text>
          </Pressable>

          {/* Header */}
          <View style={{ marginTop: 16 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9,
                  letterSpacing: 1.8,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                {`Deliverable ${row.sequence}`}
              </Text>
            </View>
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.displayMedium,
                fontSize: 30,
                lineHeight: 36,
                color: W,
              }}
            >
              {row.name || "Edit deliverable"}
            </Text>
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: WM,
              }}
            >
              {draftOnly
                ? "Edit anything — changes save when you click Save."
                : "Read-only — this case has been published."}
            </Text>
          </View>

          {err ? (
            <View
              style={{
                marginTop: 18,
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderRadius: 3,
                borderWidth: 1,
                borderColor: "rgba(230,154,138,0.45)",
                backgroundColor: "rgba(230,154,138,0.10)",
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{err}</Text>
            </View>
          ) : null}

          {/* SECTION: BASICS */}
          <SectionCard title="Basics" overline="What students submit">
            <FieldLabel required>Name</FieldLabel>
            <DarkInput value={name} onChangeText={setName} editable={draftOnly} placeholder="e.g. Final pitch deck" heading />
            <View style={{ height: 18 }} />
            <FieldLabel hint="Optional — explain what students should produce.">Description</FieldLabel>
            <DarkInput
              value={description}
              onChangeText={setDescription}
              editable={draftOnly}
              multiline
              minHeight={110}
              placeholder="What you’re looking for, how it should be structured, what makes it strong."
            />
          </SectionCard>

          {/* SECTION: FORMAT */}
          <SectionCard title="Format & template" overline="File type" subtle>
            <FieldLabel>File type</FieldLabel>
            <PillRow>
              {FILE_TYPES.map((ft) => (
                <PillBtn key={ft} selected={fileType === ft} onPress={() => draftOnly && setFileType(ft)} disabled={!draftOnly}>
                  {FILE_LABEL[ft]}
                </PillBtn>
              ))}
            </PillRow>
            <View style={{ height: 18 }} />
            <FieldLabel hint={`Optional. Picker is filtered to ${FILE_LABEL[fileType]} files.`}>Replace template</FieldLabel>
            <View style={{ marginTop: 8, flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Pressable
                onPress={() => draftOnly && void pickTemplate()}
                disabled={!draftOnly}
                style={[
                  {
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: BLACK_SURFACE,
                    opacity: draftOnly ? 1 : 0.5,
                  },
                  draftOnly ? cursorWeb : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: W }}>
                  {templateAsset ? `✓ ${templateAsset.name}` : "Choose file…"}
                </Text>
              </Pressable>
              {templateAsset ? (
                <Pressable onPress={() => setTemplateAsset(null)} style={cursorWeb}>
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: WM }}>Remove</Text>
                </Pressable>
              ) : null}
            </View>
          </SectionCard>

          {/* SECTION: SCORING */}
          <SectionCard title="Scoring" overline="How judges grade" subtle>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <PillBtn selected={isScored} onPress={() => draftOnly && setIsScored((v) => !v)} disabled={!draftOnly}>
                Scored
              </PillBtn>
              <PillBtn selected={allowRevisions} onPress={() => draftOnly && setAllowRevisions((v) => !v)} disabled={!draftOnly}>
                Allow revisions
              </PillBtn>
            </View>
            {isScored ? (
              <>
                <View style={{ height: 18 }} />
                <FieldLabel hint="Total points this deliverable is worth.">Point value</FieldLabel>
                <DarkInput value={pointValue} onChangeText={setPointValue} editable={draftOnly} keyboardType="number-pad" placeholder="10" />
              </>
            ) : null}
            <View style={{ height: 18 }} />
            <FieldLabel hint="Optional. What judges should look for.">Rubric</FieldLabel>
            <DarkInput
              value={rubric}
              onChangeText={setRubric}
              editable={draftOnly}
              multiline
              minHeight={110}
              placeholder="e.g. Clarity (40%), insight (35%), evidence (25%)."
            />
          </SectionCard>

          {/* SECTION: SCHEDULE */}
          <SectionCard title="Schedule" overline="When it’s available">
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <PillBtn selected={isLockedGate} onPress={() => draftOnly && setIsLockedGate((v) => !v)} disabled={!draftOnly}>
                Locked gate
              </PillBtn>
            </View>
            <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11, color: WL, lineHeight: 16 }}>
              {isLockedGate
                ? "Locked: students see this deliverable only after its Opens at time."
                : "Unlocked: students see it as soon as the competition opens."}
            </Text>
            <View style={{ height: 18 }} />
            <View style={{ flexDirection: "row", gap: 14 }}>
              <View style={{ flex: 1 }}>
                <FieldLabel>Opens at</FieldLabel>
                <FriendlyDateTime
                  value={opensAt}
                  onChange={setOpensAt}
                  editable={draftOnly}
                  clearable
                  placeholder="Set a custom open date"
                />
              </View>
              <View style={{ flex: 1 }}>
                <FieldLabel>Due at</FieldLabel>
                <FriendlyDateTime
                  value={dueAt}
                  onChange={setDueAt}
                  editable={draftOnly}
                  clearable
                  placeholder="Set a custom due date"
                />
              </View>
            </View>
            <Text style={{ marginTop: 8, fontSize: 10.5, lineHeight: 15, color: WL, fontFamily: FONTS.body }}>
              Optional — left unset, the deliverable opens with the case and is due at case close. Your local time.
            </Text>
          </SectionCard>

          {/* Actions */}
          <View
            style={{
              marginTop: 28,
              paddingTop: 18,
              borderTopWidth: 1,
              borderTopColor: BORDER,
              flexDirection: "row",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Pressable
              onPress={() => void onSave()}
              disabled={saving || !draftOnly}
              style={[
                {
                  paddingHorizontal: 22,
                  paddingVertical: 12,
                  borderRadius: 3,
                  backgroundColor: !draftOnly || saving ? "rgba(181,135,58,0.4)" : AMBER,
                },
                cursorWeb,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: THEME.sidebarBg,
                }}
              >
                {saving ? "Saving…" : "Save changes"}
              </Text>
            </Pressable>
            <Pressable
              onPress={goBack}
              disabled={saving}
              style={[
                {
                  paddingHorizontal: 18,
                  paddingVertical: 12,
                  borderRadius: 3,
                  borderWidth: 1,
                  borderColor: BORDER,
                  backgroundColor: BLACK_SURFACE,
                },
                cursorWeb,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: WS,
                }}
              >
                Cancel
              </Text>
            </Pressable>
          </View>
        </View>
    </ScrollView>
  );
}

// ===========================================================================
// Inline themed primitives — same look as the case editor.
// ===========================================================================

function SectionCard({ title, overline, subtle, children }: { title: string; overline: string; subtle?: boolean; children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 26 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View style={{ width: 18, height: 2, backgroundColor: AMBER, opacity: 0.7 }} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", color: AMBER }}>
          {overline}
        </Text>
      </View>
      <Text style={{ marginTop: 4, fontFamily: FONTS.display, fontSize: 22, lineHeight: 26, color: W }}>
        {title}
      </Text>
      <View
        style={{
          marginTop: 14,
          padding: 22,
          borderRadius: 4,
          backgroundColor: subtle ? "rgba(255,255,255,0.015)" : OFF_BLACK,
          borderWidth: 1,
          borderColor: BORDER,
        }}
      >
        {children}
      </View>
    </View>
  );
}

function FieldLabel({ children, required, hint }: { children: string; required?: boolean; hint?: string }) {
  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 5 }}>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", color: WS }}>
          {children}
        </Text>
        {required ? <Text style={{ color: RED, fontFamily: FONTS.bodyBold, fontSize: 9 }}>*</Text> : null}
      </View>
      {hint ? <Text style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>{hint}</Text> : null}
    </View>
  );
}

function DarkInput({
  value,
  onChangeText,
  editable = true,
  multiline,
  minHeight,
  placeholder,
  keyboardType,
  heading,
}: {
  value: string;
  onChangeText: (t: string) => void;
  editable?: boolean;
  multiline?: boolean;
  minHeight?: number;
  placeholder?: string;
  keyboardType?: "default" | "number-pad" | "email-address";
  heading?: boolean;
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      editable={editable}
      multiline={multiline}
      keyboardType={keyboardType}
      placeholder={placeholder}
      placeholderTextColor={WL}
      textAlignVertical={multiline ? "top" : "auto"}
      style={{
        marginTop: 8,
        backgroundColor: BLACK_SURFACE,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 3,
        paddingHorizontal: 14,
        paddingVertical: 12,
        minHeight: minHeight ?? undefined,
        color: editable ? W : WM,
        fontFamily: heading ? FONTS.displayMedium : FONTS.body,
        fontSize: heading ? 18 : 13.5,
        lineHeight: heading ? undefined : 20,
        opacity: editable ? 1 : 0.7,
      }}
    />
  );
}

function PillRow({ children }: { children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {children}
    </View>
  );
}

function PillBtn({
  selected,
  onPress,
  children,
  disabled,
}: {
  selected: boolean;
  onPress: () => void;
  children: string;
  disabled?: boolean;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 8,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: selected ? AMBER : BORDER,
          backgroundColor: selected ? "rgba(181,135,58,0.12)" : BLACK_SURFACE,
          opacity: disabled ? 0.55 : 1,
        },
        !disabled ? cursorWeb : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: selected ? AMBER : WS }}>
        {children}
      </Text>
    </Pressable>
  );
}
